import { Comment } from '@/models/registry';
import { CommentTargetType, type Comment as CommentType } from '@/models/types';
import {
  type IObservableArray,
  makeAutoObservable,
  observable,
  type ObservableMap,
} from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

class CommentStore implements BaseStore<Comment, CommentType> {
  store: ObservableMap<string, Comment> = observable.map();
  private _empty: IObservableArray<Comment> = observable.array([], {
    deep: false,
  });

  // Indexes for different target types
  storeIndexedByDealId: ObservableMap<string, Comment[]> = observable.map();
  storeIndexedByTradeId: ObservableMap<string, Comment[]> = observable.map();
  storeIndexedByAppraisalId: ObservableMap<string, Comment[]> =
    observable.map();

  private commentTypeToIndexMap = {
    [CommentTargetType.Deal]: this.storeIndexedByDealId,
    [CommentTargetType.Trade]: this.storeIndexedByTradeId,
    [CommentTargetType.Appraisal]: this.storeIndexedByAppraisalId,
  };

  constructor() {
    makeAutoObservable(
      this,
      {
        store: observable.shallow,
        storeIndexedByDealId: observable.shallow,
        storeIndexedByTradeId: observable.shallow,
      },
      { autoBind: true },
    );
  }

  private getOrCreateCollection(
    map: Map<string, Comment[]>,
    key: string,
  ): Comment[] {
    let arr = map.get(key);
    if (!arr) {
      arr = observable.array<Comment>([], { deep: false });
      map.set(key, arr);
    }
    return arr;
  }

  private setCommentField = <K extends keyof CommentType>(
    comment: Comment,
    key: K,
    value: CommentType[K],
  ) => {
    (comment as Pick<Comment, K>)[key] = value as Comment[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CommentType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.comments.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.comments.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.comments.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.comments.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<CommentType>;
  }) {
    const existingComment = this.getById(delta.record_id);
    if (!existingComment) return;

    const oldTargetId = existingComment.commentable_id;
    const oldTargetType = existingComment.commentable_type;

    let changed = false;
    let indexFieldsChanged = false;

    const entries = Object.entries(delta.data) as [
      keyof CommentType,
      CommentType[keyof CommentType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingComment[key] === newValue) {
        continue;
      }

      this.setCommentField(existingComment, key, newValue);
      changed = true;

      if (key === 'commentable_id' || key === 'commentable_type') {
        indexFieldsChanged = true;
      }
    }

    if (!changed) return;
    this.save(
      existingComment,
      indexFieldsChanged ? { oldTargetId, oldTargetType } : undefined,
    );
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<CommentType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CommentType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.comments.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.remove(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  getByTarget(targetId: string, targetType: CommentTargetType) {
    const indexMap = this.commentTypeToIndexMap[targetType];
    if (!indexMap) return this._empty;
    return this.getOrCreateCollection(indexMap, targetId);
  }

  addToIndexes(comment: Comment) {
    const { commentable_id: targetId, commentable_type: targetType } = comment;
    const indexMap = this.commentTypeToIndexMap[targetType];

    this.getOrCreateCollection(indexMap, targetId).push(comment);
  }

  removeFromIndexes(comment: Comment) {
    const { commentable_id: targetId, commentable_type: targetType } = comment;
    const indexMap = this.commentTypeToIndexMap[targetType];

    const removeFrom = (arr: Comment[]) => {
      const i = arr.findIndex((c) => c.id === comment.id);
      if (i >= 0) arr.splice(i, 1);
    };

    removeFrom(this.getOrCreateCollection(indexMap, targetId));
  }

  // Computed getters - automatically update when store changes
  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): Comment | undefined {
    return this.store.get(id);
  }

  add(input: CommentType): void {
    const comment = new Comment(input);

    this.store.set(comment.id, comment);
    this.addToIndexes(comment);
  }

  save(
    comment: Comment,
    opts?: { oldTargetId?: string; oldTargetType?: CommentTargetType },
  ) {
    this.store.set(comment.id, comment);

    const arr = this.getByTarget(
      comment.commentable_id,
      comment.commentable_type,
    );
    const idx = arr.findIndex((c) => c.id === comment.id);
    if (idx >= 0) arr.splice(idx, 1, comment);
    else arr.push(comment);

    if (
      opts?.oldTargetId &&
      opts?.oldTargetType &&
      (opts.oldTargetId !== comment.commentable_id ||
        opts.oldTargetType !== comment.commentable_type)
    ) {
      const oldArr = this.getByTarget(opts.oldTargetId, opts.oldTargetType);
      const oldIdx = oldArr.findIndex((c) => c.id === comment.id);
      if (oldIdx >= 0) oldArr.splice(oldIdx, 1);
    }
  }

  upsert(comment: CommentType) {
    const existingComment = this.getById(comment.id);
    if (existingComment) {
      existingComment.content = comment.content;
      existingComment.edited = comment.edited;
      existingComment.updated_at = comment.updated_at;
      this.save(existingComment);
    } else {
      this.add(comment);
    }
  }

  remove(id: CommentType['id']) {
    const comment = this.getById(id);
    if (!comment) return;
    this.store.delete(id);
    // remove from the index
    this.removeFromIndexes(comment);

    // remove from dexie
    db.comments.delete(id);
  }

  replace({ id, comment }: { id: string; comment: CommentType }) {
    const existingComment = this.getById(id);
    if (!existingComment) return;

    existingComment.content = comment.content;
    existingComment.edited = comment.edited;
    existingComment.updated_at = comment.updated_at;
    // existingComment.mentions = comment.mentions;
    this.save(existingComment);
  }

  // TODO - table and entity name should prob be generic comments?
  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_comments',
      entity: 'deal_comments',
      modelStore: commentStore,
    });
  }

  async hydrateDeal(dealId: CommentType['commentable_id']): Promise<void> {
    const items = await db.comments.toArray();
    items.forEach((item) => {
      if (
        item.commentable_id === dealId &&
        item.commentable_type === CommentTargetType.Deal
      )
        this.upsert(item);
    });
  }
}

export const commentStore = new CommentStore();
